import React from 'react';

import {AnalysisGraphQL, GetAnalysisPipelineStep} from 'analysis-tool/AnalysisTypes';
import getFlaggingActions from 'analysis-tool/data-view/analysis-adapter/getFlaggingActions';
import {getViewForViewReference} from 'analysis-tool/data-view/AnalysisViewUtils';
import {ButtonDataViewAction} from 'analysis-tool/data-view/DataViewContext';
import {ViewReference} from 'analysis-tool/ui/main-view/MainViewTypes';
import {ChartNode, Chart} from 'charts/ChartTypes';
import {DataFrame} from 'data-frames/DataFrameTypes';
import {PersistedDataFrameGraphQL} from 'data-frames/wrapper/PersistedDataFrameTableWrapper';
import {useRolloutFlags} from 'launchdarkly/LaunchDarklyUtil';
import {getDefaultClientIdForView} from 'pipelines/PipelineViewUtils';
import Button, {ButtonStyle, ButtonSize} from 'ui-library/buttons/Button';
import Tooltip from 'ui-library/floating-ui/Tooltip';
/**
 * Gets flagging actions for a chart view by converting viewSteps to a view.
 *
 * This is the chart-specific adapter that bridges the gap between:
 * - Chart components (which work with viewSteps)
 * - The shared getFlaggingActions utility (which works with PipelineViewGraphQL)
 *
 * @returns Flagging actions that can be converted to buttons, or null if flagging is not available
 */
function getFlaggingActionsForChartView({
  analysis,
  viewSteps,
  selectedRowIndices,
}: {
  analysis: AnalysisGraphQL;
  viewSteps: Array<GetAnalysisPipelineStep>;
  selectedRowIndices?: Set<number>;
}): ReturnType<typeof getFlaggingActions> | null {
  if (!selectedRowIndices || selectedRowIndices.size === 0) {
    return null;
  }

  // Build a ViewReference from the last step in viewSteps
  const lastStep = viewSteps[viewSteps.length - 1];
  const outputStepData = lastStep.outputStepDatas[0];
  if (!outputStepData) {
    return null;
  }

  const viewReference: ViewReference = {
    stepId: lastStep.id,
    outputReference: {
      stepDataName: outputStepData.name,
      rawDataIndex: 0,
    },
  };

  const view = getViewForViewReference(analysis, viewReference);
  if (!view) {
    return null;
  }

  const viewClientId = getDefaultClientIdForView(view);

  const flaggingActions = getFlaggingActions(analysis, view, viewClientId, selectedRowIndices);

  if (flaggingActions.length === 0) {
    return null;
  }

  return flaggingActions;
}

/**
 * Renders flagging buttons (Flag/Clear) for chart views with visual point selection.
 *
 * This component:
 * 1. Gets flagging actions from the shared utility via getFlaggingActionsForChartView
 * 2. Converts them to JSX button elements with consistent styling
 *
 * The buttons automatically include:
 * - Validation (large table checks, downstream flag step checks)
 * - Proper disabled states with helpful messages
 * - State cleanup after flagging
 *
 * @param allowVisualFlagging - Whether the chart supports visual flagging (based on has_row_indices metadata)
 */
export function FlaggingButtons({
  analysis,
  viewSteps,
  selectedRowIndices,
  dataFrame,
  allowVisualFlagging,
}: {
  analysis: AnalysisGraphQL;
  viewSteps: Array<GetAnalysisPipelineStep>;
  selectedRowIndices?: Set<number>;
  dataFrame: DataFrame;
  allowVisualFlagging: boolean;
}): JSX.Element | null {
  const {enableAnalysisToolVisualFlagging: enableAnalysisToolVisualFlaggingFlag} = useRolloutFlags();

  if (!allowVisualFlagging || !enableAnalysisToolVisualFlaggingFlag) {
    return null;
  }

  const flaggingActions = getFlaggingActionsForChartView({
    analysis,
    viewSteps,
    selectedRowIndices,
  });

  if (!flaggingActions) {
    return null;
  }

  // Get the dataFrameId from the first input dataframe of the last step
  const lastStep = viewSteps[viewSteps.length - 1];
  const dataFrameId = lastStep.inputStepDatas[0]?.dataFrames[0]?.id;

  if (!dataFrameId) {
    return null;
  }

  const persistedDataFrame = {
    dataFrameJson: dataFrame,
    numRows: dataFrame.rows.length,
    id: dataFrameId,
    allColumnsJson: dataFrame.columns,
  } as PersistedDataFrameGraphQL;

  const buttonConfigs = flaggingActions
    .filter((action): action is Extract<ButtonDataViewAction, Function> => action != null)
    .map((action) => action({persistedDataFrame}))
    .filter((config) => config != null);

  if (buttonConfigs.length === 0) {
    return null;
  }

  return (
    <>
      {buttonConfigs.map((config, index) => (
        <Tooltip key={index} text={config.helpText}>
          <Button
            size={ButtonSize.MINI}
            buttonStyle={
              config.isAlternativeButtonColor ? ButtonStyle.TERTIARY_BLUE : ButtonStyle.TERTIARY_GRAY
            }
            className="u-mini-spacing-left"
            icon={config.icon}
            onClick={config.onClick}
            disabled={config.isDisabled}
            text={config.label}
          />
        </Tooltip>
      ))}
    </>
  );
}

/**
 * Type for the _benchling_metadata property in chart library configs.
 * This metadata is added by benchling-charts to indicate chart capabilities.
 */
interface BenchlingChartMetadata {
  has_row_indices?: boolean;
}

/**
 * Extracts the has_row_indices flag from the chart's library config metadata.
 * This flag indicates whether the chart supports row-index-based point selection for flagging.
 */
export function getChartHasRowIndices(chart?: Chart | ChartNode | null): boolean {
  if (!chart) {
    return false;
  }
  const libraryConfig = chart.libraryConfig as {_benchling_metadata?: BenchlingChartMetadata};
  return libraryConfig._benchling_metadata?.has_row_indices ?? false;
}
